// src/components/tour/tours/siteDashboard.jsx
//
// Site Dashboard tour - used by BOTH SiteDashboard.jsx (internal admins)
// and ClientSiteDashboard.jsx (clients). FleetHeader's scope picks the
// wording; admin-only parts (search, stat cards, unassigned devices,
// live event feed) are tagged scopes: ['admin'] and also simply don't
// exist on the client page.

import React from 'react';
import { replayStep } from './common';

const isPendingRoam = (d) => d.device_type === 'starlink' && d.service === 'roam';

// Same state rule the dashboard uses for its STATE column.
function siteState(site) {
    const counted = site.devices.filter((d) => !isPendingRoam(d));
    const offline = counted.filter((d) => d.online === false).length;
    if (site.devices.length === 0) return { state: 'none', offline, total: 0 };
    if (counted.length === 0) return { state: 'unknown', offline, total: site.devices.length };
    if (offline === counted.length) return { state: 'down', offline, total: site.devices.length };
    if (offline > 0) return { state: 'degraded', offline, total: site.devices.length };
    return { state: 'online', offline, total: site.devices.length };
}

const Badge = ({ kind, children }) => <span className={`ft-badge ft-badge-${kind}`}>{children}</span>;

export function getSiteDashboardSteps(ctx, env) {
    const {
        siteCount = 0,
        sitesDownCount = 0,
        attentionSitesCount = 0,
        starlinkSitesCount = 0,
        tourSite,
        tourDevice,
        canEdit,
        unassignedCount = 0,
        expandTourSite,
    } = ctx;
    const { isMobile, scope } = env;
    const isClient = scope === 'client';
    const click = isMobile ? 'Tap' : 'Click';

    const st = tourSite ? siteState(tourSite) : null;
    const isPeplink = tourDevice?.device_type === 'peplink';
    const deviceName = tourDevice?.name || tourDevice?.serial_number;

    return [
        {
            target: null,
            title: isClient ? 'Your sites at a glance' : 'Welcome to the Site Dashboard',
            content: (
                <>
                    <p>
                        {isClient ? 'Every site linked to your account' : 'Your whole fleet'} on one page:{' '}
                        <span className="pt-data">{siteCount}</span> site{siteCount === 1 ? '' : 's'},{' '}
                        <span className="pt-data">{attentionSitesCount}</span> needing attention
                        and <span className="pt-data">{sitesDownCount}</span> fully down right now.
                    </p>
                    <p>We'll walk through it with your live data. Nothing is changed during the tour.</p>
                </>
            ),
        },

        {
            scopes: ['admin'],
            target: '[data-tour="header-search"]',
            title: 'Search everything',
            content: (
                <p>
                    Find any device across the fleet by serial, model, name, firmware, ICCID, IMEI or IMSI,
                    or a Starlink kit / UT ID. Only matching sites and devices stay on screen.
                </p>
            ),
        },

        {
            scopes: ['admin'],
            layout: 'desktop',
            target: '[data-tour="sd-stats"]',
            title: 'Fleet health',
            content: (
                <>
                    <p>Sites monitored, Peplink routers online and Starlink lines online, fleet-wide.</p>
                    <p>
                        <b>Sites needing attention</b> and <b>Sites down</b> are also filters:{' '}
                        {click.toLowerCase()} one to show only those sites, {click.toLowerCase()} again to clear.
                    </p>
                </>
            ),
        },

        {
            target: '[data-tour="sd-pills"]',
            title: 'Sort & filter',
            content: (
                <p>
                    <b>ATTENTION</b> puts sites with offline devices at the top (the default).{' '}
                    {isMobile
                        ? <><b>DOWN</b> shows only sites where every device is offline ({sitesDownCount} now).</>
                        : <><b>STARLINK</b> shows only sites with a Starlink line ({starlinkSitesCount} now).</>}{' '}
                    <b>ALL</b> resets to the full list.
                </p>
            ),
        },

        {
            target: '[data-tour="sd-site"]',
            title: tourSite ? tourSite.site_name : 'A site',
            content: st ? (
                <>
                    <p>
                        One row per site. This one is{' '}
                        {st.state === 'down' && <Badge kind="down">DOWN</Badge>}
                        {st.state === 'degraded' && <Badge kind="degraded">DEGRADED</Badge>}
                        {st.state === 'online' && <Badge kind="online">ONLINE</Badge>}
                        {(st.state === 'none' || st.state === 'unknown') && <Badge kind="unknown">NO DEVICES</Badge>}
                        {' '}with <span className="pt-data">{st.total - st.offline}/{st.total}</span> devices online.
                    </p>
                    <p>
                        <Badge kind="degraded">DEGRADED</Badge> means some devices are offline,{' '}
                        <Badge kind="down">DOWN</Badge> means all of them.
                        {!isMobile && ' "Needs attention" says what exactly is wrong.'}
                    </p>
                    <p>{click} a site to open its device list.</p>
                </>
            ) : null,
        },

        {
            target: '[data-tour="sd-device"]',
            onEnter: expandTourSite,
            title: 'Devices at the site',
            content: tourDevice ? (
                <>
                    <p>
                        Each router or Starlink line at the site
                        {deviceName && <>, like <span className="pt-data">{deviceName}</span></>}, with its live state{' '}
                        {tourDevice.online
                            ? <Badge kind="online">ONLINE</Badge>
                            : <Badge kind="down">OFFLINE</Badge>}
                        {tourDevice.uptime && !isMobile && ' and uptime'}.
                    </p>
                    <p>
                        {isMobile ? 'Tap the card' : 'Click its name'} to open the full{' '}
                        {isPeplink ? <b>Monitoring report</b> : <b>Starlink page</b>}
                        {isPeplink ? ' (usage, WAN history, logs, PDF export).' : ' (usage and plan details).'}
                    </p>
                </>
            ) : null,
        },

        {
            when: () => isPeplink,
            target: '[data-tour="sd-device-stats"]',
            onEnter: expandTourSite,
            title: 'Live WAN & clients',
            content: (
                <>
                    <p>
                        {isMobile
                            ? <>Tap any of these numbers to open the live panel right here</>
                            : <>Click <b>WANS</b> or <b>CLIENTS</b> to open the live panel right here</>}
                        : current link status and every connected client.
                        {!isMobile && <> “imp” counts the important ones, and an <b>OFF</b> chip means one of them is down.</>}
                    </p>
                    <p>
                        <b>Important</b> items are the ones we watch for you: you get an alert if one goes offline.
                        {canEdit
                            ? ' From the live panel you can mark WANs/clients as important and change WAN priority.'
                            : ' Your account can view them; an admin on your account can change them.'}
                    </p>
                </>
            ),
        },

        {
            scopes: ['admin'],
            when: () => unassignedCount > 0,
            target: '[data-tour="sd-unassigned"]',
            title: 'Unassigned devices',
            content: (
                <p>
                    <span className="pt-data">{unassignedCount}</span> device{unassignedCount === 1 ? '' : 's'} not linked to any
                    site yet. They're listed here with their live state; assign them to a site in <b>Site Manager</b>.
                </p>
            ),
        },

        {
            scopes: ['admin'],
            target: '[data-tour="sd-events"]',
            title: 'Live event feed',
            content: <p>The latest status changes across the fleet (devices going up or down, Starlink overage), refreshed every 15 seconds.</p>,
        },

        replayStep,
    ];
}
